import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import type { CustomReport } from "store/apis/customReports";
import {
  CUSTOM_REPORT_MODE,
  useCreateCustomReportMutation,
  useGetCustomReportsQuery,
} from "store/apis/customReports";
import { BackendError } from "types/utils/error";

export type UseCustomReports = {
  templates: CustomReport[];
  savedReports: CustomReport[];
  isLoading: boolean;
  isSuccess: boolean;
  isEmpty: boolean;
  isCreating: boolean;
  hasWritePermission: boolean;
  selectedReportId: string;
  deleteModal: ReturnType<typeof useModal>;
  handleCreateNew: () => Promise<void>;
  handleCreateFromTemplate: (templateId: string) => Promise<void>;
  handleCardClick: (report: CustomReport) => void;
  handleEdit: (report: CustomReport) => void;
  handleDeleteClick: (report: CustomReport) => void;
};

export const useCustomReports = (): UseCustomReports => {
  const { link } = useConstructInternalLink();
  const history = useHistory();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const deleteModal = useModal();

  const [selectedReportId, setSelectedReportId] = useState("");

  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.FINANCIALS
  );

  const {
    data: allReports = [],
    isLoading,
    isSuccess,
  } = useGetCustomReportsQuery({ entityId }, { skip: !entityId });

  const [createCustomReport, { isLoading: isCreating }] =
    useCreateCustomReportMutation();

  const savedReports = useMemo(
    () => allReports.filter((report) => !report.is_template),
    [allReports]
  );

  const templates = useMemo(
    () => allReports.filter((report) => report.is_template),
    [allReports]
  );

  const handleCardClick = (report: CustomReport) => {
    history.push(link(`/books/custom-reports/${report.uuid}`));
  };

  const handleEdit = (report: CustomReport) => {
    history.push(
      link("/books/custom-reports/new", {
        moreQuery: { reportId: report.uuid, mode: CUSTOM_REPORT_MODE.EDIT },
      })
    );
  };

  const handleDeleteClick = (report: CustomReport) => {
    setSelectedReportId(report.uuid);
    deleteModal.open();
  };

  const handleCreateNew = async () => {
    try {
      const response = await createCustomReport({
        entityId,
        payload: { title: "New Report" },
      }).unwrap();

      history.push(
        link("/books/custom-reports/new", {
          moreQuery: { reportId: response.uuid },
        })
      );
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    try {
      const response = await createCustomReport({
        entityId,
        payload: { template_id: templateId },
      }).unwrap();

      history.push(
        link("/books/custom-reports/new", {
          moreQuery: {
            reportId: response.uuid,
            mode: CUSTOM_REPORT_MODE.TEMPLATE,
          },
        })
      );
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return {
    templates,
    savedReports,
    isLoading,
    isSuccess,
    isEmpty: savedReports.length === 0 && templates.length === 0,
    isCreating,
    hasWritePermission,
    selectedReportId,
    deleteModal,
    handleCreateNew,
    handleCreateFromTemplate,
    handleCardClick,
    handleEdit,
    handleDeleteClick,
  };
};
